import type { TimeBand } from '@/types';

/**
 * Constantes métier d'EuroRail.
 * Tout ce qui pilote le comportement des calculs est centralisé ici
 * pour faciliter les ajustements (et les évaluer en eval/test).
 */

/** Pénalité forfaitaire de correspondance (minutes) — ajoutée à chaque arête sauf au point de départ */
export const TRANSFER_MIN = 20;

// ===== CO2 (g/passager·km) — moyennes ADEME indicatives =====

export const CO2_TRAIN = 6;
export const CO2_BUS = 30;
/** Voiture seule, ordre de grandeur pour la comparaison */
export const CO2_CAR = 150;
/** Avion direct court-courrier */
export const CO2_PLANE = 200;

// ===== Trains de nuit =====

/**
 * Bonus appliqué au coût d'un train de nuit en mode `night`.
 * Justification : 1h de train de nuit "vaut" beaucoup moins qu'1h de jour parce que
 * ça remplace une nuit d'hôtel + une journée perdue. Facteur 0.35 = on considère
 * qu'on récupère ~65% du temps. Ce coût n'est utilisé QUE pour le routing
 * (priorisation de l'algo), pas pour l'affichage à l'utilisateur.
 */
export const NIGHT_BONUS_FACTOR = 0.35;

/** Heure de départ minimale d'un train de nuit (heure locale 24h) */
export const NIGHT_TRAIN_DEPARTURE_HOUR = 21;

/** Durée minimale (minutes) pour qu'un segment de nuit "compte" comme nuit gagnée */
export const NIGHT_SEGMENT_MIN_DURATION = 360; // 6h

// ===== Persistence =====

/** Clé localStorage — versionnée pour pouvoir invalider proprement */
export const LS_KEY = 'eurorail-state-v1';

// ===== Bandes temporelles (légende + couleur des marqueurs) =====

/**
 * Bandes d'isochrones affichées sur la carte.
 * Ordre croissant strict sur `max`. Une ville à `t` minutes prend la couleur de
 * la première bande dont `max * 60 >= t`.
 */
export const BANDS: TimeBand[] = [
  { max: 1, color: '#4a1b0c', label: '< 1h' },
  { max: 2, color: '#993c1d', label: '1–2h' },
  { max: 3, color: '#d85a30', label: '2–3h' },
  { max: 4, color: '#ef9f27', label: '3–4h' },
  { max: 5, color: '#ba7517', label: '4–5h' },
  { max: 6, color: '#534ab7', label: '5–6h' },
  { max: 8, color: '#185fa5', label: '6–8h' },
  { max: 12, color: '#0f6e56', label: '8–12h' },
  { max: 24, color: '#04342c', label: '12–24h' },
];

// ===== Valeurs par défaut du state =====

export const DEFAULT_STATE = {
  origin: 'lyon' as const,
  maxHours: 6,
  routeMode: 'best' as const,
  routeDirectOnly: false,
  tripStartTime: '09:00' as const,
};

// ===== Format de date / heure (FR) =====

export const WEEKDAYS_FR = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'] as const;
export const MONTHS_SHORT_FR = [
  'janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin',
  'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.',
] as const;
